/**
 * The VV/VH series, beside the optical one — and labelled as what it is.
 *
 * THIS BLOCK IS USUALLY ABSENT, and that is the point. Since 2026-08-03 the series is
 * bought only by a run whose optics went blind (backend app/pipeline/blindness.py); on a
 * run that could read its own sky there is no series, and then there is no block, no
 * heading and no occurrence of the word "радар" anywhere on the screen. Explaining an
 * absence would put radar in front of a user for whom it played no part, and a notice about
 * something that did not happen reads as a fault.
 *
 * Since docs/UI_PLAN.md §4.4 the block is absent from the PRODUCT surface in one more case:
 * the series exists but the answer does not rest on it. Then it is context for us, it lives
 * on the developer surface, and the user's screen keeps the word "радар" out of it.
 *
 * When the answer IS the radar's the block is evidence, so it stays on the same screen as
 * the verdict — and only then does it draw the event bracket. Drawing that interval over a
 * series that decided nothing would hint at corroboration the measurement does not give:
 * radar refinement was measured and it makes DATING WORSE — the optical bracket contained
 * the labelled harvest 40/40, the refined one 26-30/40 (docs/EXP_RADAR_VALUE.md).
 */

import { T } from "../strings";
import type { Run } from "../api/types";
import { formatDate, formatDayMonthShort, num, parseDate } from "../lib/format";
import {
  decibelGrid,
  pointsByTrack,
  radarAnsweredThisRun,
  radarBlockApplies,
  radarEventOf,
  radarSeriesFromRun,
  type RadarEvent,
  type RadarPoint,
  type RadarSeriesModel,
} from "../lib/radarSeries";
import { monthTicks } from "../lib/timeline";
import { useMeasuredWidth } from "../hooks/useMeasuredWidth";
import "./RadarSeries.css";

const PAD_LEFT = 34;
const PAD_RIGHT = 12;
const PLOT_HEIGHT = 86;
/** Room under the plot for the month ticks and their labels. */
const AXIS_BAND = 24;
const BRACKET_TOP = 8;
const BRACKET_BOTTOM = 30;
/** With a bracket the plot moves down to make room for it; without one nothing changes. */
const PLOT_TOP_WITH_EVENT = 38;
const PLOT_TOP_PLAIN = 10;

const Notice = ({ decides }: { decides: boolean }) => (
  <p className={`k-note ${decides ? "k-note--warn" : "k-note--info"}`} data-testid="radar-notice">
    {decides ? T.radar.decides : T.radar.notice}
  </p>
);

function Head({ children }: { children?: string }) {
  return (
    <div className="k-section__head">
      <span className="k-label">{T.radar.title}</span>
      {children && <span className="k-hint k-mono">{children}</span>}
    </div>
  );
}

function Chart({ model, event }: { model: RadarSeriesModel; event: RadarEvent | null }) {
  const [viewWidth, wrapper] = useMeasuredWidth();
  const plotWidth = viewWidth - PAD_LEFT - PAD_RIGHT;
  const plotTop = event ? PLOT_TOP_WITH_EVENT : PLOT_TOP_PLAIN;
  const plotBottom = plotTop + PLOT_HEIGHT;
  const viewHeight = plotBottom + AXIS_BAND;
  const span = model.max - model.min;
  const xOf = (position: number): number => PAD_LEFT + position * plotWidth;
  const yOf = (db: number): number => plotBottom - ((db - model.min) / span) * PLOT_HEIGHT;

  const path = (points: RadarPoint[], pick: (point: RadarPoint) => number | null): string =>
    points
      .filter((point) => pick(point) !== null)
      .map(
        (point, index) =>
          `${index === 0 ? "M" : "L"}${xOf(point.position)} ${yOf(pick(point) as number)}`,
      )
      .join(" ");

  /** The ribbon between VV and VH: its widening IS the RFDI rise, drawn instead of stated. */
  const ribbon = (points: RadarPoint[]): string => {
    const both = points.filter((point) => point.vv !== null && point.vh !== null);
    if (both.length < 2) return "";
    const top = both.map(
      (point, index) =>
        `${index === 0 ? "M" : "L"}${xOf(point.position)} ${yOf(point.vv as number)}`,
    );
    const bottom = [...both]
      .reverse()
      .map((point) => `L${xOf(point.position)} ${yOf(point.vh as number)}`);
    return `${top.join(" ")} ${bottom.join(" ")} Z`;
  };

  const startMs = parseDate(model.windowStart)?.getTime() ?? 0;
  const endMs = parseDate(model.windowEnd)?.getTime() ?? startMs + 1;
  const ticks = monthTicks(startMs, endMs, Math.max(endMs - startMs, 1));

  return (
    <div className="k-radar" ref={wrapper}>
      <svg
        className="k-radar__svg"
        viewBox={`0 0 ${viewWidth} ${viewHeight}`}
        height={viewHeight}
        role="img"
        aria-label={T.radar.ariaChart}
      >
        {/* An axis with two numbers on it is not an axis. Every 2 dB, labelled. */}
        {decibelGrid(model).map((db) => (
          <g key={db}>
            <line
              className="k-radar__grid"
              x1={PAD_LEFT}
              x2={PAD_LEFT + plotWidth}
              y1={yOf(db)}
              y2={yOf(db)}
            />
            <text className="k-radar__axis" x={PAD_LEFT - 4} y={yOf(db) + 3} textAnchor="end">
              {num(db, 0)}
            </text>
          </g>
        ))}
        <line
          className="k-radar__baseline"
          x1={PAD_LEFT}
          x2={PAD_LEFT + plotWidth}
          y1={plotBottom}
          y2={plotBottom}
        />
        <text className="k-radar__axis" x={2} y={plotTop - 2}>
          {T.radar.axisDecibels}
        </text>

        {pointsByTrack(model).map(([track, points]) => (
          <g key={track}>
            <path className="k-radar__ribbon" d={ribbon(points)}>
              <title>{T.radar.gapTitle}</title>
            </path>
            <path className="k-radar__line k-radar__line--vv" d={path(points, (p) => p.vv)} />
            <path className="k-radar__line k-radar__line--vh" d={path(points, (p) => p.vh)} />
          </g>
        ))}

        {model.points.map((point) => {
          const label = T.radar.point(
            formatDate(point.date),
            num(point.vv, 1),
            num(point.vh, 1),
            point.track,
          );
          return (
            <g key={point.key}>
              {point.vv !== null && (
                <circle
                  className="k-radar__dot--vv"
                  cx={xOf(point.position)}
                  cy={yOf(point.vv)}
                  r={2.5}
                >
                  <title>{label}</title>
                </circle>
              )}
              {/* Hollow, not a second colour: the lines obey "state is never colour alone"
                  and the dots used to be the one place that did not. */}
              {point.vh !== null && (
                <circle
                  className="k-radar__dot--vh"
                  cx={xOf(point.position)}
                  cy={yOf(point.vh)}
                  r={2.5}
                >
                  <title>{label}</title>
                </circle>
              )}
            </g>
          );
        })}

        {/* The bracket, with the same geometry and the same label as the strip's. Only here,
            only when the answer is this series'. */}
        {event && (
          <g
            className="k-radar__bracket"
            data-testid="radar-bracket"
            data-span-days={event.spanDays}
          >
            <line
              x1={xOf(event.fromPosition)}
              y1={BRACKET_BOTTOM}
              x2={xOf(event.fromPosition)}
              y2={BRACKET_TOP + 8}
            />
            <line
              x1={xOf(event.toPosition)}
              y1={BRACKET_BOTTOM}
              x2={xOf(event.toPosition)}
              y2={BRACKET_TOP + 8}
            />
            <line
              x1={xOf(event.fromPosition)}
              y1={BRACKET_TOP + 8}
              x2={xOf(event.toPosition)}
              y2={BRACKET_TOP + 8}
            />
            <text
              className="k-radar__bracketlabel"
              x={(xOf(event.fromPosition) + xOf(event.toPosition)) / 2}
              y={BRACKET_TOP + 2}
              textAnchor="middle"
            >
              {T.radar.eventLabel(formatDayMonthShort(event.from), formatDayMonthShort(event.to))}
            </text>
          </g>
        )}

        {ticks.length > 0 ? (
          ticks.map((tick) => (
            <g key={`${tick.position}-${tick.label}`}>
              <line
                className="k-radar__tick"
                x1={xOf(tick.position)}
                y1={plotBottom}
                x2={xOf(tick.position)}
                y2={plotBottom + 5}
              />
              <text
                className="k-radar__axis"
                x={xOf(tick.position)}
                y={plotBottom + 17}
                textAnchor="middle"
              >
                {tick.label}
              </text>
            </g>
          ))
        ) : (
          <>
            <text className="k-radar__axis" x={PAD_LEFT} y={plotBottom + 17}>
              {formatDayMonthShort(model.windowStart)}
            </text>
            <text
              className="k-radar__axis"
              x={PAD_LEFT + plotWidth}
              y={plotBottom + 17}
              textAnchor="end"
            >
              {formatDayMonthShort(model.windowEnd)}
            </text>
          </>
        )}
      </svg>

      <div className="k-radar__legend k-hint">
        <span>
          <span className="k-radar__swatch k-radar__swatch--vv" aria-hidden="true" />
          {T.radar.vv}
        </span>
        <span>
          <span className="k-radar__swatch k-radar__swatch--vh" aria-hidden="true" />
          {T.radar.vh}
        </span>
      </div>
    </div>
  );
}

function Numbers({ model }: { model: RadarSeriesModel }) {
  return (
    <details className="k-details k-section k-section--tight">
      <summary>{T.radar.numbersTitle}</summary>
      <dl className="k-readout">
        {model.points.map((point) => (
          <div key={point.key} style={{ display: "contents" }}>
            <dt>{formatDate(point.date)}</dt>
            <dd className="k-mono">
              {T.radar.vv} {num(point.vv, 1)} · {T.radar.vh} {num(point.vh, 1)} ·{" "}
              {point.track || T.common.noData}
            </dd>
          </div>
        ))}
      </dl>
    </details>
  );
}

interface Props {
  run: Run | null;
  /** Developer surface (docs/UI_PLAN.md §2.2): where a series that decided nothing lives. */
  dev?: boolean;
}

export function RadarSeries({ run, dev = false }: Props) {
  // No series, no block, no mention. On a run whose optics answered, radar was never
  // fetched, played no part and must not appear anywhere on the screen.
  if (!radarBlockApplies(run)) return null;

  const decides = radarAnsweredThisRun(run);
  // A series that explains cloudy days is an instrument, not an answer: it belongs behind
  // the console's service panel. When the answer rests on it, it is evidence and stays.
  if (!decides && !dev) return null;

  const model = radarSeriesFromRun(run);

  if (model === null || model.points.length === 0) {
    return (
      <section className="k-section k-section--tight" data-testid="radar-series">
        <Head />
        <p className="k-caption">{T.radar.emptyWindow}</p>
      </section>
    );
  }

  return (
    <section className="k-section k-section--tight" data-testid="radar-series">
      <Head>{T.radar.window(formatDate(model.windowStart), formatDate(model.windowEnd))}</Head>
      <Notice decides={decides} />
      <Chart model={model} event={radarEventOf(run, model)} />
      <p className="k-hint k-mono" data-testid="radar-counts">
        {T.radar.counts(model.accepted, model.seen, model.tracks.length)}
      </p>
      {/* The dev-only "why" paragraph was removed 2026-09-05: measurement results and
          quality claims about our own paths live in docs, not on any tier of the screen.
          The one caveat the user gets is at the verdict (`T.verdict.radarLine`); `Notice`
          above is the chart's own one-line label. */}
      {/* The per-point table is an instrument reading, not an argument. */}
      {dev && <Numbers model={model} />}
    </section>
  );
}
